import React from 'react';
import omit from 'lodash';
import Select, { CSSObjectWithLabel, GroupBase, OnChangeValue, Props as SelectProps, StylesConfig } from 'react-select';
import classnames from 'classnames';
import Creatable from 'react-select/creatable';

export type DropdownOption = {
  label?: React.ReactNode;
  value: string | number;
};

export type GroupedDropdownOption = {
  label: string;
  options: DropdownOption[];
};

type DropdownProps = {
  isPlain?: boolean;
  isSmall?: boolean;
  isMedium?: boolean;
  isBig?: boolean;
  isBigSearchBox?: boolean;
  customClassName?: string;
  defaultValue?: DropdownOption | DropdownOption[] | null;
  isLoading?: boolean;
  value?: DropdownOption | DropdownOption[] | null;
  options?: DropdownOption[] | GroupedDropdownOption[];
  name?: string;
  onDropDownChange?: (value: OnChangeValue<DropdownOption, boolean>) => void;
  onInputChange?: (inputValue: string) => void;
  noOptionsMessage?: () => React.ReactNode;
  autoResize?: boolean;
  isMulti?: boolean;
  isCreatable?: boolean;
  customStyles?: StylesConfig<DropdownOption, boolean, GroupBase<DropdownOption>>;
  isReadOnly?: boolean;
  hasError?: boolean;
  isDisabled?: boolean;
  formatOptionLabel?: (option: DropdownOption) => React.ReactNode;
  isClearable?: boolean;
  placeholder?: string;
  menuIsOpen?: boolean;
  components?: SelectProps<DropdownOption, boolean>['components'];
  formatCreateLabel?: (inputValue: string) => React.ReactNode;
} & Omit<
  SelectProps<DropdownOption, boolean>,
  | 'isMulti'
  | 'value'
  | 'defaultValue'
  | 'onChange'
  | 'options'
  | 'styles'
  | 'isDisabled'
  | 'isLoading'
  | 'name'
  | 'className'
  | 'components'
  | 'placeholder'
  | 'formatOptionLabel'
  | 'formatCreateLabel'
  | 'onInputChange'
  | 'noOptionsMessage'
>;

const DropDown: React.FC<DropdownProps> = (props) => {
  const {
    isPlain,
    isSmall,
    isMedium,
    isBig,
    isBigSearchBox,
    customClassName,
    defaultValue,
    isLoading,
    value,
    options,
    name,
    onDropDownChange,
    onInputChange,
    noOptionsMessage,
    autoResize = false,
    isMulti = false,
    isCreatable = false,
    customStyles,
    isReadOnly,
    hasError = false,
    isDisabled = false,
    formatOptionLabel,
    isClearable,
    placeholder,
    menuIsOpen,
    components,
  } = props;
  const selectClass = classnames(
    'lf-select',
    {
      'lf-select--plain': isPlain,
      'lf-select--small': isSmall,
      'lf-select--medium': isMedium,
      'lf-select--big': isBig,
      'lf-select--big-searchbox': isBigSearchBox,
      'lf-select--readonly': isReadOnly,
    },
    customClassName
  );

  const handleFormatCreateLabel = (inputValue: string) => {
    return props.formatCreateLabel ? props.formatCreateLabel(inputValue) : inputValue;
  };

  const SelectComponent = isCreatable ? Creatable : Select;
  const styles: StylesConfig<DropdownOption, boolean, GroupBase<DropdownOption>> = {
    singleValue: (style) => (autoResize ? omit(style, ['maxWidth']) : { ...style }) as CSSObjectWithLabel,
    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
    menu: (base) => ({ ...base, marginTop: 2 }),
    option: (style, { isSelected }) => ({
      ...style,
      color: isSelected ? '#ffffff' : '#333333',
    }),
    control: (style) => ({
      ...style,
      ...(isReadOnly && { backgroundColor: 'transparent' }),
      ...(hasError && { borderColor: '#F44336 !important' }),
    }),
    ...customStyles,
  };

  return (
    <div className="custom-dropdown">
      <SelectComponent
        name={name ? name : ''}
        isLoading={isLoading}
        components={components}
        defaultValue={defaultValue}
        value={value}
        options={options}
        onChange={onDropDownChange}
        classNamePrefix="lf-select"
        menuPortalTarget={document.body}
        styles={styles}
        menuPosition={'absolute'}
        menuPlacement={'auto'}
        className={selectClass}
        onInputChange={onInputChange}
        isMulti={isMulti}
        formatCreateLabel={handleFormatCreateLabel}
        isDisabled={isDisabled}
        noOptionsMessage={noOptionsMessage}
        formatOptionLabel={formatOptionLabel}
        isClearable={isClearable}
        placeholder={placeholder}
        menuIsOpen={menuIsOpen}
        {...props}
      />
    </div>
  );
};

export default DropDown;
